
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface AuthProviderProps {
  children: React.ReactNode;
  onUserChange: (user: any) => void;
}

export const AuthProvider = ({ children, onUserChange }: AuthProviderProps) => {
  const [loading, setLoading] = useState(true);
  const [initialAuthCheckDone, setInitialAuthCheckDone] = useState(false);
  const sessionExpiredToastShown = useRef(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      onUserChange(session?.user ?? null);
    });

    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        onUserChange(session?.user ?? null);
        
        const hadPreviousSession = localStorage.getItem('supabase.auth.token') === 'true';
        if (initialAuthCheckDone && hadPreviousSession && !session && !sessionExpiredToastShown.current) {
          sessionExpiredToastShown.current = true;
          toast({
            title: "Session Expired",
            description: "Please sign in again to continue.",
            duration: 5000,
          });
        }
        
        if (session?.user) {
          localStorage.setItem('supabase.auth.token', 'true');
        }
      } catch (error) {
        console.error("Error checking session:", error);
      } finally {
        setLoading(false);
        setInitialAuthCheckDone(true);
      }
    };

    checkSession();

    const handleStripeReturn = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('success') || urlParams.has('canceled')) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session && localStorage.getItem('supabase.auth.token')) {
          localStorage.setItem('stripe.return', 'true');
          window.location.reload();
        }
      }
      
      if (localStorage.getItem('stripe.return')) {
        localStorage.removeItem('stripe.return');
      }
    };

    handleStripeReturn();

    return () => subscription.unsubscribe();
  }, [initialAuthCheckDone, onUserChange]);

  if (loading) {
    return null;
  }

  return <>{children}</>;
};
